import { Diamond, DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import { JewelryVariation } from "@src/types/JewelryVariationDetails";
import { useGetJewelryVariation } from "@apis/diamondDetail";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Loader from "@components/atoms/Loader";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { FlatList, Platform, useWindowDimensions } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useRoute, useNavigation } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import ImageComponentJewelry from "./ImageComponentJewelry";
import { ProductCategory } from "@utils/constants";
import JewelryConfigurationCell, { CellInfo } from "./JewelryConfigurationCell";
import LabelView from "@components/molecules/LabelView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Button, VDBText } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import currency from "currency.js";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useQueryClient } from "@tanstack/react-query";
import { useBulkActions } from "@apis/collection";
import {
  BulkActionsEnum,
  CartTypeEnum,
} from "@utils/product/searchResultConstants";
import { filterDetailsByPriceMode, getStockItemType } from "./Utils";
import {
  getProductColor,
  getProductTintColor,
} from "@utils/product/productTheme";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { navigate } from "@navigations/utils/RootNavigation";
import { GetHeaderLeft } from "@navigations/utils";
import Routes from "@navigations/Routes";
import { useTranslation } from "react-i18next";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { useSelector } from "react-redux";
import { formatItemValue } from "@components/molecules/SearchResultListItem/types";
import { useUserCurrency } from "../../hooks/getCurrency";
import { sortAllTypes } from "@scenes/configurator/JewelryConfigurator/utils";
import { css } from "styled-components/native";

type OrderNowJewelryVariationScreenProps = {
  mItem?: Diamond;
  onClose?: () => void;
  isBuyNow?: boolean;
  productType?: string;
};

const buildNestedParams = (
  prefix: string,
  obj: Record<string, string>
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const key in obj) {
    result[`${prefix}[${key}]`] = obj[key];
  }
  return result;
};

const buildApiParams = (
  selections: Record<string, string>,
  displayConfig: DisplayConfigurationV2[],
  lastChangedOptionName: string
): Record<string, string> => {
  const lastChangedIndex = displayConfig.findIndex(
    (c) => c.option_name === lastChangedOptionName
  );

  const slugDetail: Record<string, string> = {};
  const partialSlugDetail: Record<string, string> = {};

  displayConfig.forEach((configItem, index) => {
    if (!configItem.option_slug) return;

    const selectedLabel = selections[configItem.option_name];
    if (!selectedLabel) return;

    const matched = configItem.option_value.find(
      (opt) => opt.label === selectedLabel
    );
    if (matched?.value) {
      slugDetail[configItem.option_type] = matched.value;

      if (lastChangedIndex === -1 || index <= lastChangedIndex) {
        partialSlugDetail[configItem.option_type] = matched.value;
      }
    }
  });

  return {
    ...buildNestedParams("slug_details", slugDetail),
    ...buildNestedParams("partial_slug_details", partialSlugDetail),
  };
};

const makeVariantKey = (selections: Record<string, string>) =>
  JSON.stringify(Object.entries(selections).sort());

const buildConfigFromSelections = (
  selections: Record<string, string>,
  displayConfig: DisplayConfigurationV2[]
): Record<string, string> => {
  const config: Record<string, string> = {};
  displayConfig.forEach((configItem) => {
    const selectedLabel = selections[configItem.option_name];
    if (!selectedLabel) return;
    const matched = configItem.option_value.find(
      (opt) => opt.label === selectedLabel
    );
    if (matched?.value) {
      config[configItem.option_type] = matched.value;
    }
  });
  return config;
};

type ConfigEntry = {
  id: number;
  variationData?: JewelryVariation;
};

const getVariationItem = (
  variationData?: JewelryVariation
): Diamond | undefined => {
  const variationItem = variationData?.response?.body?.variations?.[0]?.item;
  if (!variationItem) return undefined;
  return {
    ...variationItem,
    display_configuration_v2: sortAllTypes(
      variationItem.display_configuration_v2 ?? []
    ),
  };
};

/** Build default selections + slug API params from a base Diamond (mItemProp). */
const buildDefaultsFromItem = (item: Diamond) => {
  const displayConfig = item.display_configuration_v2;
  const slugDetail = item.slug_detail;
  const initialSelections: Record<string, string> = {};
  const slugParams: Record<string, string> = {};

  if (!displayConfig?.length || !slugDetail) {
    return { initialSelections, slugParams, slugOnlySelections: {} };
  }

  displayConfig.forEach((configItem) => {
    const rawSlug = slugDetail[configItem.option_type];
    if (typeof rawSlug !== "string" || !rawSlug) return;
    const matched = configItem.option_value.find(
      (opt) => opt.slug === rawSlug || opt.value === rawSlug
    );
    if (matched) {
      initialSelections[configItem.option_name] = matched.label;
      if (configItem.option_slug) {
        slugParams[configItem.option_type] = matched.value;
      }
    }
  });

  const slugOnlySelections: Record<string, string> = {};
  displayConfig.forEach((configItem) => {
    if (configItem.option_slug && initialSelections[configItem.option_name]) {
      slugOnlySelections[configItem.option_name] =
        initialSelections[configItem.option_name];
    }
  });

  return { initialSelections, slugParams, slugOnlySelections };
};

const OrderNowJewelryVariationScreen = ({
  mItem: mItemProp,
  onClose,
  isBuyNow: isBuyNowProp,
  productType: productTypeProp,
}: OrderNowJewelryVariationScreenProps) => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  // Base product from mItemProp — never overwritten by variation responses.
  // Every newly added configuration is seeded from this item.
  const baseMItem: Diamond = mItemProp ?? route?.params?.mItem;
  if (baseMItem) {
    baseMItem.display_configuration_v2 = sortAllTypes(
      baseMItem.display_configuration_v2 ?? []
    );
  }
  const mItem = baseMItem;
  const isBuyNow: boolean = isBuyNowProp ?? route?.params?.isBuyNow ?? false;
  const productType: string =
    productTypeProp ?? route?.params?.productType ?? ProductCategory.JEWELRY;
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { height, width: windowWidth } = useWindowDimensions();
  const theme = useTheme();
  // On web the window height shrinks when the on-screen keyboard opens, which
  // would otherwise collapse the container (max-height is a % of this height)
  // and leave a large empty gap. Freeze to the tallest height seen so the
  // container keeps its size while the keyboard is open.
  const stableHeightRef = useRef(height);
  if (height > stableHeightRef.current) {
    stableHeightRef.current = height;
  }
  const layoutHeight = Platform.OS === "web" ? stableHeightRef.current : height;
  // On web the shrink in window height equals the on-screen keyboard height.
  // Use it to add just-enough scroll room so the focused input can clear the
  // keyboard, and none when the keyboard is closed (avoids empty bottom space).
  const webKeyboardInset =
    Platform.OS === "web" ? Math.max(0, stableHeightRef.current - height) : 0;
  const maxHeight = layoutHeight * 0.7;
  const mobileMaxHeight =
    theme.isMobile && Platform.OS === "web"
      ? layoutHeight * 0.77
      : layoutHeight * 0.73;
  const tabletMaxHeight =
    theme.isTablet && Platform.OS === "web"
      ? layoutHeight * 0.8
      : layoutHeight * 0.76;
  const userInfo = useUserInfo();
  const userCurrency = useUserCurrency();
  const typography = useTypographyStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (navigation?.setOptions) {
      const bgColorKey = getProductColor(productType);
      const tintColorKey = getProductTintColor(productType);
      navigation.setOptions({
        title: isBuyNow
          ? t("orderNowModal.buyNow.title.text", "Buy Now")
          : t("orderNowModal.addToCart.title.text", "Add to Cart"),
        headerStyle: {
          backgroundColor: theme[bgColorKey],
        },
        headerTintColor: theme[tintColorKey],
        headerLeft: () => <GetHeaderLeft canGoBack textColor={tintColorKey} />,
      });
    }
  }, [isBuyNow, navigation, t, theme, productType]);
  const priceMode: number = (userInfo as any)?.user?.price_mode ?? 1;
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const customSearchData = useCustomSearchData();
  const productDetailData =
    customSearchData[ProductCategory.JEWELRY]?.product_details?.options;
  const filteredProductDetails = filterDetailsByPriceMode(
    productDetailData,
    priceMode
  );
  const bulkActionsCall = useBulkActions();
  const queryClient = useQueryClient();

  const [isApiLoading, setIsApiLoading] = useState(true);
  const startLoading = () => setIsApiLoading(true);
  const stopLoading = () => setIsApiLoading(false);

  const [configs, setConfigs] = useState<ConfigEntry[]>([{ id: 0 }]);
  const [cellSelections, setCellSelections] = useState<
    Record<number, Record<string, string>>
  >({});

  // Keyed by option_name → label; used only as a stable cache key.
  const [activeVariantParams, setActiveVariantParams] = useState<Record<
    string,
    string
  > | null>(null);

  // The actual query params sent to the API (slug_details[option_type] format).
  const [activeApiParams, setActiveApiParams] = useState<Record<
    string,
    string
  > | null>(null);

  // Stores the selections state of a cell just before a user-triggered API
  // call so we can revert if the combination comes back empty.
  const prevCellSelectionsRef = useRef<{
    cellId: number;
    selections: Record<string, string>;
  } | null>(null);

  // When true, onSettled will skip calling onClose() so the modal stays open
  // and the user can see the error toast rendered inside the modal.
  const skipCloseRef = useRef(false);

  const [cellInfo, setCellInfo] = useState<Record<number, CellInfo>>({});

  const [activeCellId, setActiveCellId] = useState<number>(0);

  // On mount: seed cell 0 selections and fire the initial variation API call
  // using baseMItem.slug_detail — mirrors PDP's immediate first fetch.
  useEffect(() => {
    const { initialSelections, slugParams, slugOnlySelections } =
      buildDefaultsFromItem(baseMItem);

    // Always seed cell 0 with defaults for correct UI pre-selection.
    // Use a functional updater so we MERGE with whatever child useEffects
    // (e.g. OrderNowDropDown single-option auto-fire) already queued — a
    // plain replacement would overwrite them because parent effects run last.
    if (Object.keys(initialSelections).length > 0) {
      setCellSelections((prev) => ({
        ...prev,
        0: { ...(prev[0] ?? {}), ...initialSelections },
      }));
    }

    // Only fire variation API if there are slug-contributing options.
    if (Object.keys(slugParams).length === 0) return;

    setActiveVariantParams(slugOnlySelections);
    setActiveApiParams({
      ...buildNestedParams("slug_details", slugParams),
      ...buildNestedParams("partial_slug_details", slugParams),
    });
  }, []);

  const computedNetPrice = Object.values(cellInfo).reduce(
    (sum, info) => sum + (info.totalPrice ?? 0),
    0
  );
  // Before any cell reports its price (e.g. right after mount) the computed
  // total is 0; fall back to the item's total_sales_price in that case.
  const totalNetPrice =
    computedNetPrice === 0
      ? Number(mItem?.total_sales_price) || 0
      : computedNetPrice;

  const getCellItem = (cellId: number): Diamond => {
    const config = configs.find((c) => c.id === cellId);
    // Fall back to base mItemProp until this cell has its own variationData.
    return getVariationItem(config?.variationData) ?? baseMItem;
  };

  // Each config validates against its own variation-derived display config
  // (falling back to the base mItem when that cell has no variation yet).
  // Also require a resolved variantId so confirmOrder can include every cell.
  const isAllConfigsComplete = configs.every((cell) => {
    const cellItem = getCellItem(cell.id);
    const visibleRequiredOptions = (
      cellItem?.display_configuration_v2 ?? []
    ).filter((option) => option.option_visible);
    return (
      visibleRequiredOptions.length > 0 &&
      visibleRequiredOptions.every(
        (option) => !!cellSelections[cell.id]?.[option.option_name]
      ) &&
      cellInfo[cell.id]?.variantId != null
    );
  });

  const activeVariantKey = activeVariantParams
    ? makeVariantKey(activeVariantParams)
    : "";

  const { data: variationData, isFetching: isVariationFetching } =
    useGetJewelryVariation(
      mItem?.id as number,
      priceMode,
      1,
      true,
      activeApiParams ?? undefined
    );

  useEffect(() => {
    const jewelryData = (variationData as any)?.data as
      | JewelryVariation
      | undefined;
    if (!jewelryData) return;

    const variations = jewelryData?.response?.body?.variations;

    if (variations && variations.length === 0) {
      // Combination not available — revert to previous selections if this was
      // triggered by a user interaction (prevCellSelectionsRef is set).
      if (prevCellSelectionsRef.current) {
        const { cellId, selections } = prevCellSelectionsRef.current;
        prevCellSelectionsRef.current = null;

        setCellSelections((prev) => ({ ...prev, [cellId]: selections }));

        // Revert activeVariantParams to the previous slug-only key so the
        // FlatList can still resolve the last valid cached variation.
        const displayConfig = mItem?.display_configuration_v2 ?? [];
        const prevSlugOnly: Record<string, string> = {};
        displayConfig.forEach((configItem) => {
          if (configItem.option_slug && selections[configItem.option_name]) {
            prevSlugOnly[configItem.option_name] =
              selections[configItem.option_name];
          }
        });
        setActiveVariantParams(
          Object.keys(prevSlugOnly).length > 0 ? prevSlugOnly : null
        );
        setActiveApiParams(null);
      }

      showToast(
        "",
        t(
          "searchResult.jewelry.combinationNotFound.text",
          "This combination is not available. Please change your selection."
        ),
        ToastTypes.ERROR,
        true,
        true,
        80
      );
      return;
    }

    // Persist this response onto the active config only — mItem stays fixed.
    setConfigs((prev) =>
      prev.map((config) =>
        config.id === activeCellId
          ? { ...config, variationData: jewelryData }
          : config
      )
    );

    const variationItem = variations?.[0]?.item;
    const allowedKeys =
      variationItem?.display_configuration_v2?.map(
        (item) => item.option_name
      ) ?? [];
    setCellSelections((prev) => {
      const currentCell = prev[activeCellId] ?? {};

      const updatedCell = Object.fromEntries(
        Object.entries(currentCell).filter(([key]) => allowedKeys.includes(key))
      ) as Record<string, string>;

      allowedKeys.forEach((key) => {
        if (!(key in updatedCell)) {
          const configObject = variationItem?.display_configuration_v2?.find(
            (item) => item.option_name === key
          );
          const option_type = configObject?.option_type;
          const option_value = variationItem?.slug_detail?.[option_type ?? ""];

          updatedCell[key] =
            configObject?.option_value.find(
              (item) => item.slug === option_value
            )?.value ?? "";
        }
      });

      return {
        ...prev,
        [activeCellId]: updatedCell,
      };
    });

    setIsApiLoading(false);
  }, [variationData, activeVariantKey]);

  const addNewConfiguration = () => {
    const newId = configs.length;
    const { initialSelections, slugParams, slugOnlySelections } =
      buildDefaultsFromItem(baseMItem);

    // When the new cell uses the same base slug params as the current query,
    // react-query returns a cache hit and the variation effect does not re-run.
    // Attach the already-fetched base variation so the cell gets a variantId
    // and is included in the confirmOrder payload.
    const jewelryData = (variationData as any)?.data as
      | JewelryVariation
      | undefined;
    const baseKey = makeVariantKey(slugOnlySelections);
    const currentKey = activeVariantParams
      ? makeVariantKey(activeVariantParams)
      : "";
    const seedVariationData =
      jewelryData &&
      Object.keys(slugOnlySelections).length > 0 &&
      currentKey === baseKey
        ? jewelryData
        : undefined;

    // Always seed selections from base mItemProp — never copy another cell.
    setConfigs((prev) => [
      {
        id: newId,
        ...(seedVariationData ? { variationData: seedVariationData } : {}),
      },
      ...prev,
    ]);
    setCellSelections((prev) => ({
      ...prev,
      [newId]: { ...initialSelections },
    }));
    setActiveCellId(newId);

    if (Object.keys(slugParams).length > 0) {
      setActiveVariantParams(slugOnlySelections);
      setActiveApiParams({
        ...buildNestedParams("slug_details", slugParams),
        ...buildNestedParams("partial_slug_details", slugParams),
      });
    }
  };

  const handleSelectionChange = (
    cellId: number,
    optionName: string,
    value: string
  ) => {
    if (optionName === "quantity") return;
    setActiveCellId(cellId);
    setCellSelections((prev) => {
      const updatedCell = { ...(prev[cellId] ?? {}), [optionName]: value };
      const updatedAll = { ...prev, [cellId]: updatedCell };

      const cellItem = getCellItem(cellId);
      const displayConfig = cellItem?.display_configuration_v2 ?? [];
      const allSelected = displayConfig
        .filter((option) => option.option_slug)
        .every((option) => !!updatedCell[option.option_name]);

      if (allSelected) {
        const slugOnlyCell: Record<string, string> = {};
        displayConfig.forEach((configItem) => {
          if (configItem.option_slug && updatedCell[configItem.option_name]) {
            slugOnlyCell[configItem.option_name] =
              updatedCell[configItem.option_name];
          }
        });
        prevCellSelectionsRef.current = {
          cellId,
          selections: prev[cellId] ?? {},
        };
        setActiveVariantParams(slugOnlyCell);
        setActiveApiParams(
          buildApiParams(updatedCell, displayConfig, optionName)
        );
      }

      return updatedAll;
    });
  };

  const formattedPrice = (mPrice: number) => {
    const totalValue = currency(mPrice, {
      symbol: userInfo?.user?.currency_symbol,
      precision: 0,
    }).format();
    return totalValue;
  };

  const dismiss = () => {
    if (theme.isMobile || theme.isTablet) {
      navigation?.goBack?.();
    } else {
      onClose?.();
    }
  };

  const confirmOrder = () => {
    const itemIds: number[] = [];
    const itemCartAttributes: Record<
      string,
      { quantity: number; config: Record<string, string> }
    > = {};

    configs.forEach((cell) => {
      const info = cellInfo[cell.id];
      const selections = cellSelections[cell.id];
      const variantId = info?.variantId;
      const cellItem = getCellItem(cell.id);
      const displayConfig = cellItem?.display_configuration_v2 ?? [];

      if (variantId != null) {
        const key = String(variantId);
        const qty = info.quantity ?? 1;

        if (itemCartAttributes[key]) {
          itemCartAttributes[key].quantity += qty;
        } else {
          itemIds.push(variantId);
          itemCartAttributes[key] = {
            quantity: qty,
            config: selections
              ? buildConfigFromSelections(selections, displayConfig)
              : {},
          };
        }
      }
    });

    startLoading();
    bulkActionsCall.mutate(
      {
        action_type: BulkActionsEnum.BULK_REQUEST_ACTION,
        stock_type: getStockItemType(ProductCategory.JEWELRY),
        cart_type: CartTypeEnum.ORDER_CART,
        item_ids: itemIds,
        price_mode: priceMode,
        item_cart_attributes: itemCartAttributes,
        ...(isBuyNow && { buy_now: true }),
      },
      {
        onSuccess(data) {
          if (data?.data?.response?.header?.status === 200) {
            if (!isBuyNow) {
              queryClient
                .invalidateQueries({ queryKey: ["GET_CART_ITEM"] })
                .then();
            }
            const body = data?.data?.response?.body;
            const message = data?.data?.response?.header?.message;
            const notAddedData = body?.not_added_data;
            const failedCount =
              notAddedData?.not_added_due_to_is_customizable ?? 0;
            const allFailed =
              failedCount > 0 && failedCount === itemIds?.length;
            const someFailed = failedCount > 0;

            if (allFailed) {
              // All items failed — keep the modal open so the user can see the
              // error toast rendered by the modal-scoped <Toast />.
              skipCloseRef.current = true;
              showToast(
                "",
                `(${failedCount})` +
                  t(
                    "global.addToCart.message.text",
                    "item(s) could not be added due to technical limitations "
                  ),
                ToastTypes.ERROR,
                true,
                true,
                80
              );
            } else if (someFailed) {
              if (!isBuyNow) {
                // Close the modal and delay the toast so the modal's inner
                // <Toast /> fully unmounts before Toast.show() fires,
                // ensuring the global toast instance picks it up.
                dismiss();
                skipCloseRef.current = true;
                setTimeout(() => {
                  showToast(
                    "",
                    `(${failedCount})` +
                      t(
                        "global.addToCart.message.text",
                        "item(s) could not be added due to technical limitations "
                      ),
                    ToastTypes.SUCCESS,
                    true,
                    true,
                    80
                  );
                }, 400);
              } else {
                showToast(
                  "",
                  `(${failedCount})` +
                    t(
                      "global.addToCart.message.text",
                      "item(s) could not be added due to technical limitations "
                    ),
                  ToastTypes.SUCCESS,
                  true,
                  true,
                  80
                );
              }
            } else if (!isBuyNow) {
              // Close the modal and delay the toast so the modal's inner
              // <Toast /> fully unmounts before Toast.show() fires,
              // ensuring the global toast instance picks it up.
              dismiss();
              skipCloseRef.current = true;
              setTimeout(() => {
                showToast("", message, ToastTypes.SUCCESS, true, true, 80);
              }, 400);
            }

            // Navigate only when at least one item was successfully added.
            if (!allFailed && isBuyNow && itemIds?.length !== failedCount) {
              navigate(Routes.BulkRequestNavigator, {
                screen: Routes.OrderCartScreen,
                params: {
                  isBuyNow: true,
                  buyNowSessionId: body?.buy_now_session_id,
                  productSubType: body?.product_sub_type,
                },
              });
            }
          }
        },
        onSettled() {
          stopLoading();
          if (skipCloseRef.current) {
            skipCloseRef.current = false;
          } else {
            onClose?.();
          }
        },
      }
    );
  };

  const renderConfigCell = (item: ConfigEntry, index: number) => {
    const isLast = index === configs.length - 1;
    const isFirst = index === 0;
    const cellItem = getCellItem(item.id);
    return (
      <JewelryConfigurationCell
        key={item.id.toString()}
        mItem={cellItem}
        variationData={item.variationData}
        isLast={isLast}
        isFirst={isFirst}
        selections={cellSelections[item.id]}
        onSelectionChange={(optionName, value) =>
          handleSelectionChange(item.id, optionName, value)
        }
        onInfoChange={(info) =>
          setCellInfo((prev) => ({ ...prev, [item.id]: info }))
        }
      />
    );
  };

  const content = (
    <ParentContainer
      theme={theme}
      isMobile={theme.isMobile}
      isTablet={theme.isTablet}
      isNativeMobile={theme.isMobile && Platform.OS !== "web"}
    >
      {(isVariationFetching || isApiLoading) && <Loader />}
      <InnerParentContainer
        theme={theme}
        maxHeight={maxHeight}
        isMobile={theme.isMobile}
        isTablet={theme.isTablet}
        tabletMaxHeight={tabletMaxHeight}
        mobileMaxHeight={mobileMaxHeight}
      >
        <ImageContainer theme={theme}>
          <ImageComponentJewelry
            mItem={mItem}
            addNewConfig={addNewConfiguration}
          />
        </ImageContainer>
        <ProductDetailsContainer>
          {filteredProductDetails?.map((item: any, index: number) => (
            <LabelView
              key={item.name ?? index}
              itemLeftText={t(item.label)}
              itemRightText={formatItemValue(
                isCustomApp,
                item.response_key,
                mItem,
                userCurrency,
                userInfo,
                t,
                true
              )}
              keyWidthPercent={35}
              valueWidthPercent={65}
              keyTypographyStyle={typography["Minor-R"]}
              valueTypographyStyle={typography["Minor-M"]}
              ellipsize={false}
              alignKeyTop
            />
          ))}
        </ProductDetailsContainer>
        {Platform.OS === "web" ? (
          // Web (incl. responsive mobile/tablet) has no native keyboard, so the
          // browser handles bringing the focused input into view. Keep the
          // original FlatList here to avoid the keyboard-aware over-scroll.
          <FlatList
            data={configs}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item, index }) => renderConfigCell(item, index)}
            nestedScrollEnabled
            style={theme.isMobile ? { flex: 1 } : undefined}
            contentContainerStyle={{
              paddingBottom: webKeyboardInset > 0 ? webKeyboardInset + 90 : 0,
            }}
          />
        ) : (
          <KeyboardAwareScrollView
            nestedScrollEnabled
            bottomOffset={90}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            style={{ flex: 1 }}
          >
            {configs.map((item, index) => renderConfigCell(item, index))}
          </KeyboardAwareScrollView>
        )}
      </InnerParentContainer>
      {theme.isMobile ? (
        <TotalPriceAndButtonContainerMobile
          theme={theme}
          bottomInset={bottomInset}
        >
          <PriceLabelGroup>
            <VDBText type={typography["H3-R"]} color={theme["core-05"]}>
              {t("searchResult.totalPrice.text", "Total Price")}
            </VDBText>
            <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
              {totalNetPrice !== null ? formattedPrice(totalNetPrice) : ""}
            </VDBText>
          </PriceLabelGroup>
          <Button
            category={ButtonCategory.jewelry}
            title={t("searchResult.confirmOrder.text", "Confirm Order")}
            type={ButtonType.primary}
            disabled={!isAllConfigsComplete}
            onPress={() => {
              confirmOrder();
            }}
          />
        </TotalPriceAndButtonContainerMobile>
      ) : (
        <TotalPriceAndButtonContainer theme={theme} isTablet={theme.isTablet}>
          <HalfContainer side="left" isTablet={theme.isTablet}>
            <LabelView
              itemLeftText={t("searchResult.totalPrice.text", "Total Price")}
              itemRightText={
                totalNetPrice !== null ? formattedPrice(totalNetPrice) : ""
              }
              cellWidth={220}
              isFixedKeyWidth={true}
              keyWidth={80}
              ellipsize={false}
              keyTypographyStyle={typography["H3-R"]}
              valueTypographyStyle={typography["H3-M"]}
              isFixWidth
            />
          </HalfContainer>
          <HalfContainer side="right" isTablet={theme.isTablet}>
            <Button
              category={ButtonCategory.jewelry}
              title={t("searchResult.confirmOrder.text", "Confirm Order")}
              type={ButtonType.primary}
              disabled={!isAllConfigsComplete}
              maxWidth={theme.isTablet ? windowWidth * 0.43 : undefined}
              onPress={() => {
                confirmOrder();
              }}
            />
          </HalfContainer>
        </TotalPriceAndButtonContainer>
      )}
    </ParentContainer>
  );

  return theme.isMobile || theme.isTablet ? (
    <LayoutContainer>{content}</LayoutContainer>
  ) : (
    content
  );
};

const ParentContainer = styled.View<any>`
  width: 100%;
  background-color: ${({ theme }) => theme["core-02"]};
  position: relative;
  ${({ isMobile, isTablet }) => (isMobile || isTablet) && "flex: 1;"}
`;

const InnerParentContainer = styled.View<any>`
  margin: ${({ isMobile }) => (isMobile ? "8px" : "16px")};
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  ${({ isMobile, isTablet, maxHeight, tabletMaxHeight, mobileMaxHeight }) =>
    isMobile
      ? css`
          flex: 1;
          min-height: 0;
          max-height: ${mobileMaxHeight}px;
        `
      : !isMobile &&
        (isTablet
          ? css`
              width: 608px;
              align-self: center;
              flex: 1;
              min-height: 0;
              max-height: ${tabletMaxHeight}px;
            `
          : css`
              max-height: ${maxHeight}px;
            `)};
`;

const ProductDetailsContainer = styled.View`
  width: 100%;
  padding: 16px;
  gap: 4px;
`;

const TotalPriceAndButtonContainer = styled.View<any>`
  width: 100%;
  height: 72px;
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: row;
  z-index: 2;
`;

const TotalPriceAndButtonContainerMobile = styled.View<any>`
  background-color: ${({ theme }) => theme["core-01"]};
  align-items: center;
  padding-horizontal: 24px;
  padding-bottom: ${({ bottomInset }) => (bottomInset ?? 0) + 8}px;
  z-index: 2;
`;

const PriceLabelGroup = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-horizontal: 16px;
  padding-top: 8px;
  padding-bottom: 16px;
`;

const HalfContainer = styled.View<{
  side?: "left" | "right";
  isTablet?: boolean;
}>`
  flex: 1;
  align-items: ${({ side, isTablet }) =>
    isTablet ? (side === "right" ? "flex-end" : "flex-start") : "center"};
  justify-content: center;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: ${({ side }) => (side === "right" ? "8px" : "24px")};
  padding-right: ${({ side }) => (side === "right" ? "24px" : "8px")};
`;

const ImageContainer = styled.View<any>``;

export default OrderNowJewelryVariationScreen;
